import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Lock, Unlock } from 'lucide-react';
import {
  getScheduleWeeks, getScheduleWeek, createScheduleWeek, patchScheduleDay,
  finalizeScheduleWeek, resyncScheduleWeek, duplicateScheduleWeek, getOpponents, getPlayers,
} from '../api';
import BodyPartSelector from '../components/BodyPartSelector';

// ── UTC-safe date helpers (frontend has no shared twin of the backend's addDaysUTC/
// toDateStr — those were added to scenarioDays.js's CommonJS copy only — so this page
// keeps its own small copies rather than growing the shared module for one page). ──
function toDateStr(d) {
  if (typeof d === 'string') return d.slice(0, 10);
  return d.toISOString().slice(0, 10);
}
function addDays(dateStr, n) {
  const [y, m, d] = dateStr.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + n);
  return toDateStr(dt);
}
function mondayOnOrAfter(dateStr) {
  const [y, m, d] = dateStr.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  const dow = dt.getUTCDay(); // 0=Sun..6=Sat
  const diff = dow === 0 ? 1 : dow === 1 ? 0 : 8 - dow;
  return addDays(dateStr, diff);
}
// Monday of the week containing dateStr (on or before it) — used to open Week Schedule on
// today's own week rather than skipping ahead to the next unscheduled one.
function mondayOnOrBefore(dateStr) {
  const [y, m, d] = dateStr.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  const dow = dt.getUTCDay(); // 0=Sun..6=Sat
  const diff = dow === 0 ? -6 : 1 - dow;
  return addDays(dateStr, diff);
}
// Both formatters normalize through toDateStr first — the API returns Postgres DATE
// columns as full timestamps (e.g. "2026-07-27T00:00:00.000Z"), and splitting that
// directly on '-' would leave the time/zone suffix stuck onto the day number.
function fmtHeaderDate(dateStr) {
  const [, m, d] = toDateStr(dateStr).split('-');
  return `${d}/${m}`;
}
function fmtWeekRange(startStr) {
  const end = addDays(startStr, 6);
  return `${fmtHeaderDate(startStr)} – ${fmtHeaderDate(end)}`;
}
// dd-mm-yy for the day card header, e.g. "20-07-26".
function fmtDayFull(dateStr) {
  const [y, m, d] = toDateStr(dateStr).split('-');
  return `${d}-${m}-${y.slice(2)}`;
}

// Blanks every manually-editable schedule_day column back to the same defaults
// insertAutoFilledDay uses for a brand-new day (server.production.js/server.staging.js) —
// "clear week" means "back to how this week looked the moment it was created," not a
// separate reset shape. Deliberately excludes md_type/is_match_day/target bands and the
// td/hid/sd/short_actions/hmld columns: those are auto-filled/resync-owned, never part of
// SCHEDULE_DAY_EDITABLE_FIELDS, and clearing them here would fight the next Resync.
const DEFAULT_DAY_FIELDS = {
  session_am_type: 'none', session_pm_type: 'none',
  body_parts: { front: [], back: [] }, gym_player_focus: {},
  prep_warmup_field: false, prep_passing_drill: false,
  tussenvorm: null, ssg_format: null, area_per_player: null,
  notes: null, wissels_extra: false,
  rpe_low: null, rpe_high: null, min_low: null, min_high: null,
  kickoff_time: null, opponent_name: null, home_away: null,
};

const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const SESSION_TYPES = [
  { id: 'none', label: '—' },
  { id: 'pitch', label: 'Pitch' },
  { id: 'gym', label: 'Gym' },
  { id: 'individual', label: 'Individual' },
];
const MIN_OPTIONS = Array.from({ length: 19 }, (_, i) => i * 5); // 0..90 step 5
const RPE_OPTIONS = Array.from({ length: 10 }, (_, i) => i + 1); // 1..10
const CHART_BAR_MAX = 100; // px, tallest bar in the intensity chart

const fmt0 = n => (n == null || n === '' || isNaN(n) ? '—' : Math.round(Number(n)).toLocaleString());
const fmtRange = (lo, hi) => (lo == null && hi == null) ? '—' : `${fmt0(lo)}–${fmt0(hi)}`;

// Shared by the day grid and the chart below it so Saturday's bar lands exactly under
// Saturday's column — same column template + gap on both grids.
const GRID_COLS = 'repeat(7, 1fr) 200px';
const GRID_GAP = 10;

const card = { background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 14 };
const label = { fontSize: '0.68rem', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4, display: 'block' };
// opacity: 1 + explicit -webkit-text-fill-color override the browser's own default
// dimming of disabled form controls — a finalized (read-only) week should look identical
// to an editable one, with only the lock icon signaling the difference.
const inputStyle = {
  width: '100%', padding: '6px 8px', borderRadius: 7, border: '1px solid var(--border)',
  background: 'var(--bg-app)', color: 'var(--text-primary)', fontSize: '0.8rem', fontFamily: 'inherit', boxSizing: 'border-box',
  opacity: 1, WebkitTextFillColor: 'var(--text-primary)',
};
const selectStyle = { ...inputStyle, cursor: 'pointer' };

// ── Segmented button group ──────────────────────────────────────────────────────
function SegButtons({ options, value, onChange, disabled }) {
  return (
    <div style={{ display: 'flex', gap: 3 }}>
      {options.map(opt => (
        <button key={opt.id} type="button" disabled={disabled}
          onClick={() => onChange(opt.id)}
          style={{
            flex: 1, padding: '5px 4px', borderRadius: 6, fontSize: '0.7rem', fontFamily: 'inherit',
            fontWeight: value === opt.id ? 600 : 400,
            border: value === opt.id ? '1px solid #3b82f6' : '1px solid var(--border)',
            background: value === opt.id ? '#3b82f6' : 'var(--bg-app)',
            color: value === opt.id ? '#fff' : 'var(--text-secondary)',
            cursor: disabled ? 'default' : 'pointer', opacity: 1,
          }}>
          {opt.label}
        </button>
      ))}
    </div>
  );
}

// ── Gym per-player breakdown popup ──────────────────────────────────────────────────
// Separate from the whole-team Gym focus body diagram above it — that one tags which
// body regions the session as a whole targets, this ticks per-player which of
// upper/lower/other applies to each individual (or none at all, meaning no gym for them
// that day). Keyed by player id so it survives squad_number/position edits.
const GYM_COLS = [
  { id: 'upper', label: 'Upper body' },
  { id: 'lower', label: 'Lower body' },
  { id: 'other', label: 'Other' },
];

function GymBreakdownModal({ day, dayIndex, players, isReadOnly, updateField, onClose }) {
  const focus = day.gym_player_focus || {};

  const toggle = (playerId, colId) => {
    if (isReadOnly) return;
    const key = String(playerId);
    const current = focus[key] || {};
    const next = { ...focus, [key]: { ...current, [colId]: !current[colId] } };
    updateField(dayIndex, 'gym_player_focus', next, { immediate: true });
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 24 }}
      onClick={onClose}>
      <div style={{ ...card, width: 520, maxHeight: '82vh', display: 'flex', flexDirection: 'column' }}
        onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', borderBottom: '1px solid var(--border)' }}>
          <div>
            <div style={{ fontSize: '0.9rem', fontWeight: 700, color: 'var(--text-primary)' }}>Gym — per player</div>
            <div style={{ fontSize: '0.74rem', color: 'var(--text-muted)', marginTop: 2 }}>
              {DAY_NAMES[dayIndex]} {fmtDayFull(day.schedule_date)}
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 20, lineHeight: 1, padding: 4 }}>×</button>
        </div>

        <div style={{ overflowY: 'auto', padding: '4px 18px' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left', fontSize: '0.68rem', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', padding: '10px 6px', position: 'sticky', top: 0, background: 'var(--bg-card)' }}>Player</th>
                {GYM_COLS.map(c => (
                  <th key={c.id} style={{ textAlign: 'center', fontSize: '0.68rem', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', padding: '10px 6px', position: 'sticky', top: 0, background: 'var(--bg-card)' }}>{c.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {players.map(p => {
                const row = focus[String(p.id)] || {};
                return (
                  <tr key={p.id} style={{ borderTop: '1px solid var(--border)' }}>
                    <td style={{ padding: '8px 6px', fontSize: '0.8rem', color: 'var(--text-primary)' }}>
                      {p.squad_number != null && <span style={{ color: 'var(--text-muted)', marginRight: 6 }}>{p.squad_number}</span>}
                      {p.name}
                    </td>
                    {GYM_COLS.map(c => (
                      <td key={c.id} style={{ textAlign: 'center', padding: '8px 6px' }}>
                        <input type="checkbox" checked={!!row[c.id]} disabled={isReadOnly}
                          onChange={() => toggle(p.id, c.id)} />
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div style={{ padding: '12px 18px', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={actionBtn}>Done</button>
        </div>
      </div>
    </div>
  );
}

// ── One day's cells ───────────────────────────────────────────────────────────────
// Renders as 6 separate grid items (a background card + 5 section cells) sharing the same
// grid row track as every other day column, instead of one self-contained box per day. CSS
// Grid then auto-equalizes each row's height across all 7 columns, so e.g. the "targets"
// row sits at the same y position everywhere — match days (which skip most sections) no
// longer end up with their targets floating much higher than a full training day's.
function dayColumnCells({ day, dayIndex, isReadOnly, updateField, opponents, openGymModal }) {
  const isMatch = day.is_match_day;
  // Both Dagindeling toggles left on '—' (none/none) — no AM or PM session at all — reads
  // as an off day, shaded grey the same way a match day gets shaded red.
  const isOffDay = !isMatch && (day.session_am_type || 'none') === 'none' && (day.session_pm_type || 'none') === 'none';
  const col = dayIndex + 1;
  const cellStyle = { gridColumn: `${col} / ${col + 1}`, padding: '0 12px' };

  return [
    <div key={`bg-${day.id}`} style={{
      gridColumn: `${col} / ${col + 1}`, gridRow: '1 / 6', borderRadius: 14,
      background: isMatch ? 'rgba(239,68,68,0.08)' : isOffDay ? 'rgba(148,163,184,0.12)' : 'var(--bg-card)',
      border: isMatch ? '1px solid rgba(239,68,68,0.35)' : isOffDay ? '1px solid rgba(148,163,184,0.3)' : '1px solid var(--border)',
    }} />,

    <div key={`header-${day.id}`} style={{ ...cellStyle, gridRow: '1 / 2', paddingTop: 12, paddingBottom: 10 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
        <span style={{ fontSize: '0.82rem', fontWeight: 700, color: 'var(--text-primary)' }}>
          {DAY_NAMES[dayIndex]} - {fmtDayFull(day.schedule_date)}
        </span>
      </div>
      <div style={{ marginTop: 4 }}>
        <span style={{
          fontSize: '0.66rem', fontWeight: 600, padding: '2px 7px', borderRadius: 99,
          background: 'var(--bg-app)', border: '1px solid var(--border)', color: 'var(--text-secondary)',
        }}>
          {day.md_type || 'Unscheduled'}
        </span>
      </div>
      {isMatch && (() => {
        const logoUrl = day.opponent_name
          ? opponents?.find(o => o.name.toLowerCase() === day.opponent_name.toLowerCase())?.logo_url
          : null;
        return (
          <div style={{ marginTop: 6, fontSize: '0.74rem', color: 'var(--text-primary)' }}>
            <div style={{ display: 'flex', gap: 4, marginBottom: 5 }}>
              <button type="button" disabled={isReadOnly}
                onClick={() => updateField(dayIndex, 'home_away', 'home', { immediate: true })}
                style={{
                  fontSize: '0.64rem', fontWeight: 700, padding: '1px 8px', borderRadius: 99,
                  border: day.home_away === 'home' ? '1px solid #10b981' : '1px solid var(--border)',
                  background: day.home_away === 'home' ? 'rgba(16,185,129,0.2)' : 'var(--bg-app)',
                  color: day.home_away === 'home' ? '#10b981' : 'var(--text-secondary)',
                  cursor: isReadOnly ? 'default' : 'pointer', fontFamily: 'inherit',
                }}>
                Home
              </button>
              <button type="button" disabled={isReadOnly}
                onClick={() => updateField(dayIndex, 'home_away', 'away', { immediate: true })}
                style={{
                  fontSize: '0.64rem', fontWeight: 700, padding: '1px 8px', borderRadius: 99,
                  border: day.home_away === 'away' ? '1px solid #3b82f6' : '1px solid var(--border)',
                  background: day.home_away === 'away' ? 'rgba(59,130,246,0.2)' : 'var(--bg-app)',
                  color: day.home_away === 'away' ? '#3b82f6' : 'var(--text-secondary)',
                  cursor: isReadOnly ? 'default' : 'pointer', fontFamily: 'inherit',
                }}>
                Away
              </button>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
              {logoUrl && <img src={logoUrl} alt="" style={{ width: 20, height: 20, objectFit: 'contain', borderRadius: 4 }} />}
              <input style={{ ...inputStyle, fontWeight: 600 }} disabled={isReadOnly}
                placeholder="Opponent" defaultValue={day.opponent_name || ''}
                onChange={e => updateField(dayIndex, 'opponent_name', e.target.value)} />
            </div>
            <input type="time" value={day.kickoff_time || ''} disabled={isReadOnly}
              onChange={e => updateField(dayIndex, 'kickoff_time', e.target.value, { immediate: true })}
              style={{ ...inputStyle, padding: '4px 6px' }} />
          </div>
        );
      })()}
    </div>,

    // Dagindeling / Gym / PREP / Tussenvorm-SSG-Area — doesn't apply on a match day.
    <div key={`body-${day.id}`} style={{ ...cellStyle, gridRow: '2 / 3', display: 'flex', flexDirection: 'column', gap: 10, paddingBottom: 10 }}>
      {!isMatch && (
        <>
          <div>
            <span style={label}>AM</span>
            <SegButtons options={SESSION_TYPES} value={day.session_am_type || 'none'} disabled={isReadOnly}
              onChange={v => updateField(dayIndex, 'session_am_type', v, { immediate: true })} />
            <span style={{ ...label, marginTop: 8 }}>PM</span>
            <SegButtons options={SESSION_TYPES} value={day.session_pm_type || 'none'} disabled={isReadOnly}
              onChange={v => updateField(dayIndex, 'session_pm_type', v, { immediate: true })} />
          </div>

          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={label}>Gym focus</span>
              <button type="button" onClick={() => openGymModal(dayIndex)}
                style={{
                  fontSize: '0.64rem', fontWeight: 600, padding: '2px 7px', borderRadius: 99,
                  border: '1px solid var(--border)', background: 'var(--bg-app)',
                  color: 'var(--text-secondary)', cursor: 'pointer', fontFamily: 'inherit', marginBottom: 4,
                }}>
                Per player
              </button>
            </div>
            <BodyPartSelector value={day.body_parts || { front: [], back: [] }} disabled={isReadOnly}
              onChange={v => updateField(dayIndex, 'body_parts', v, { immediate: true })} />
          </div>

          <div>
            <span style={label}>PREP</span>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.78rem', color: 'var(--text-secondary)', marginBottom: 4 }}>
              <input type="checkbox" checked={!!day.prep_warmup_field} disabled={isReadOnly}
                onChange={e => updateField(dayIndex, 'prep_warmup_field', e.target.checked, { immediate: true })} />
              Warm-up on field
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.78rem', color: 'var(--text-secondary)' }}>
              <input type="checkbox" checked={!!day.prep_passing_drill} disabled={isReadOnly}
                onChange={e => updateField(dayIndex, 'prep_passing_drill', e.target.checked, { immediate: true })} />
              Passing drill
            </label>
          </div>

          <div>
            <span style={label}>Tussenvorm</span>
            <input style={inputStyle} disabled={isReadOnly} defaultValue={day.tussenvorm || ''}
              onChange={e => updateField(dayIndex, 'tussenvorm', e.target.value)} />
          </div>
          <div>
            <span style={label}>SSG format</span>
            <input style={inputStyle} disabled={isReadOnly} defaultValue={day.ssg_format || ''}
              onChange={e => updateField(dayIndex, 'ssg_format', e.target.value)} />
          </div>
          <div>
            <span style={label}>Area / player</span>
            <input style={inputStyle} disabled={isReadOnly} defaultValue={day.area_per_player || ''}
              onChange={e => updateField(dayIndex, 'area_per_player', e.target.value)} />
          </div>
        </>
      )}
    </div>,

    // Notes — every day, including match days.
    <div key={`notes-${day.id}`} style={{ ...cellStyle, gridRow: '3 / 4', paddingBottom: 10 }}>
      <span style={label}>Notes</span>
      <textarea style={{ ...inputStyle, minHeight: 80, resize: 'vertical' }} disabled={isReadOnly}
        defaultValue={day.notes || ''}
        onChange={e => updateField(dayIndex, 'notes', e.target.value)} />
    </div>,

    // Min / RPE / Wissels extra — doesn't apply on a match day.
    <div key={`minrpe-${day.id}`} style={{ ...cellStyle, gridRow: '4 / 5', display: 'flex', flexDirection: 'column', gap: 8, paddingBottom: 10 }}>
      {!isMatch && (
        <>
          <div style={{ display: 'flex', gap: 6 }}>
            <div style={{ flex: 1 }}>
              <span style={label}>Min low</span>
              <select style={selectStyle} disabled={isReadOnly} value={day.min_low ?? ''}
                onChange={e => updateField(dayIndex, 'min_low', e.target.value === '' ? null : Number(e.target.value), { immediate: true })}>
                <option value="">—</option>
                {MIN_OPTIONS.map(v => <option key={v} value={v}>{v}</option>)}
              </select>
            </div>
            <div style={{ flex: 1 }}>
              <span style={label}>Min high</span>
              <select style={selectStyle} disabled={isReadOnly} value={day.min_high ?? ''}
                onChange={e => updateField(dayIndex, 'min_high', e.target.value === '' ? null : Number(e.target.value), { immediate: true })}>
                <option value="">—</option>
                {MIN_OPTIONS.map(v => <option key={v} value={v}>{v}</option>)}
              </select>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 6 }}>
            <div style={{ flex: 1 }}>
              <span style={label}>RPE low</span>
              <select style={selectStyle} disabled={isReadOnly} value={day.rpe_low ?? ''}
                onChange={e => updateField(dayIndex, 'rpe_low', e.target.value === '' ? null : Number(e.target.value), { immediate: true })}>
                <option value="">—</option>
                {RPE_OPTIONS.map(v => <option key={v} value={v}>{v}</option>)}
              </select>
            </div>
            <div style={{ flex: 1 }}>
              <span style={label}>RPE high</span>
              <select style={selectStyle} disabled={isReadOnly} value={day.rpe_high ?? ''}
                onChange={e => updateField(dayIndex, 'rpe_high', e.target.value === '' ? null : Number(e.target.value), { immediate: true })}>
                <option value="">—</option>
                {RPE_OPTIONS.map(v => <option key={v} value={v}>{v}</option>)}
              </select>
            </div>
          </div>

          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.78rem', color: 'var(--text-secondary)' }}>
            <input type="checkbox" checked={!!day.wissels_extra} disabled={isReadOnly}
              onChange={e => updateField(dayIndex, 'wissels_extra', e.target.checked, { immediate: true })} />
            Wissels extra
          </label>
        </>
      )}
    </div>,

    // Auto target numbers (read-only) — every day, including match days, same row for all.
    <div key={`targets-${day.id}`} style={{
      ...cellStyle, gridRow: '5 / 6', paddingBottom: 12, paddingTop: 8,
      borderTop: '1px dashed var(--border)', fontSize: '0.72rem', color: 'var(--text-muted)',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>TD</span><span style={{ color: 'var(--text-secondary)' }}>{fmtRange(day.td_low, day.td_high)}</span></div>
      <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>HID</span><span style={{ color: 'var(--text-secondary)' }}>{fmtRange(day.hid_low, day.hid_high)}</span></div>
      <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>SD</span><span style={{ color: 'var(--text-secondary)' }}>{fmtRange(day.sd_low, day.sd_high)}</span></div>
      <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Short act.</span><span style={{ color: 'var(--text-secondary)' }}>{fmtRange(day.short_actions_low, day.short_actions_high)}</span></div>
      <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>HMLD</span><span style={{ color: 'var(--text-secondary)' }}>{fmtRange(day.hmld_low, day.hmld_high)}</span></div>
    </div>,
  ];
}

// ── Main page ─────────────────────────────────────────────────────────────────────
export default function WeekSchedule({ user }) {
  const canEdit = !!(user?.isAdmin || user?.role === 'staff');

  const [weeks, setWeeks] = useState([]);
  const [selectedWeekStart, setSelectedWeekStart] = useState(null);
  const [week, setWeek] = useState(null);
  // Bumped after Clear week so the day grid remounts — the notes/tussenvorm/ssg/area/opponent
  // inputs below are uncontrolled (defaultValue), so a setWeek() with the same day.id's but
  // blanked-out field values wouldn't otherwise be reflected in already-mounted DOM nodes.
  const [resetKey, setResetKey] = useState(0);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [forceEdit, setForceEdit] = useState(false);
  const [opponents, setOpponents] = useState([]);
  const [players, setPlayers] = useState([]);
  const [gymModalDayIndex, setGymModalDayIndex] = useState(null);

  useEffect(() => { getOpponents().then(rows => setOpponents(rows || [])).catch(() => {}); }, []);
  useEffect(() => {
    getPlayers()
      .then(rows => setPlayers((rows || []).filter(p => p.active !== false)))
      .catch(() => {});
  }, []);

  const saveTimers = useRef({});

  const refreshWeeksList = useCallback(() => {
    return getScheduleWeeks().then(rows => {
      const visible = canEdit ? (rows || []) : (rows || []).filter(w => w.status === 'final');
      setWeeks(visible);
      return visible;
    });
  }, [canEdit]);

  // Initial load: pick a sensible default week.
  useEffect(() => {
    refreshWeeksList().then(visible => {
      const today = toDateStr(new Date());
      if (canEdit) {
        setSelectedWeekStart(mondayOnOrBefore(today));
      } else {
        const sorted = [...visible].sort((a, b) => b.week_start_date.localeCompare(a.week_start_date));
        setSelectedWeekStart(sorted[0] ? toDateStr(sorted[0].week_start_date) : mondayOnOrAfter(today));
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selectedWeekStart) return;
    setLoading(true);
    setForceEdit(false);
    getScheduleWeek(selectedWeekStart)
      .then(w => setWeek(w))
      .finally(() => setLoading(false));
  }, [selectedWeekStart]);

  const goPrev = () => setSelectedWeekStart(prev => addDays(prev, -7));
  const goNext = () => setSelectedWeekStart(prev => addDays(prev, 7));

  const previousWeek = useMemo(() => {
    if (!selectedWeekStart) return null;
    const prevDate = addDays(selectedWeekStart, -7);
    return weeks.find(w => toDateStr(w.week_start_date) === prevDate) || null;
  }, [weeks, selectedWeekStart]);

  // Sequential (not Promise.all) so a mid-week failure doesn't leave the days that already
  // saved racing the ones that didn't against the same setWeek call.
  const blankDayFields = async days => {
    const updated = [];
    for (const day of days) {
      updated.push(await patchScheduleDay(day.id, { ...DEFAULT_DAY_FIELDS, requesterRole: user?.role }));
    }
    return updated;
  };

  // Also reachable once a week already exists (e.g. right after Clear week) — week_start_date
  // is UNIQUE on weekly_schedule, so a second createScheduleWeek call for the same date would
  // just 500. Recreating in place (resync's auto-fill regen + a manual-field blank) produces
  // the identical end state a fresh create would, without violating that constraint or needing
  // a delete-and-reinsert round trip.
  const handleCreate = async () => {
    if (week) {
      if (!window.confirm(`Recreate week of ${fmtWeekRange(selectedWeekStart)}? This regenerates match/target info and blanks every day's manual fields — same result as creating this week fresh.`)) return;
      setBusy(true);
      try {
        const resynced = await resyncScheduleWeek(week.id);
        const cleared = await blankDayFields(resynced);
        setWeek(prev => ({ ...prev, days: cleared }));
        setResetKey(k => k + 1);
      } finally { setBusy(false); }
      return;
    }
    setBusy(true);
    try {
      const created = await createScheduleWeek({ week_start_date: selectedWeekStart, created_by: user?.id });
      setWeek(created);
      await refreshWeeksList();
    } finally { setBusy(false); }
  };

  const handleDuplicatePrevious = async () => {
    if (!previousWeek) return;
    setBusy(true);
    try {
      const created = await duplicateScheduleWeek(previousWeek.id, selectedWeekStart);
      setWeek(created);
      await refreshWeeksList();
    } finally { setBusy(false); }
  };

  const handlePrint = () => {
    if (!selectedWeekStart) return;
    localStorage.setItem('print_week_schedule', selectedWeekStart);
    window.open('/print/week-schedule', '_blank');
  };

  const handleFinalize = async () => {
    if (!week) return;
    setBusy(true);
    try {
      const updated = await finalizeScheduleWeek(week.id);
      setWeek(prev => ({ ...prev, ...updated }));
      await refreshWeeksList();
    } finally { setBusy(false); }
  };

  const handleResync = async () => {
    if (!week) return;
    setBusy(true);
    try {
      const updatedDays = await resyncScheduleWeek(week.id);
      setWeek(prev => ({ ...prev, days: updatedDays }));
    } finally { setBusy(false); }
  };

  // Wipes every day's manual fields (session types, gym/body parts, notes, RPE/min ranges,
  // kickoff time, opponent/home-away) back to blank — the auto-filled columns (md_type,
  // is_match_day, target bands) are untouched since those aren't user input to begin with.
  const handleClearWeek = async () => {
    if (!week) return;
    if (!window.confirm(`Clear week of ${fmtWeekRange(selectedWeekStart)}? This wipes every day's session types, gym/body parts, notes, RPE/min ranges, kickoff time, and opponent — targets and match-day status stay as-is.`)) return;
    setBusy(true);
    try {
      const updatedDays = await blankDayFields(week.days);
      setWeek(prev => ({ ...prev, days: updatedDays }));
      setResetKey(k => k + 1);
    } finally { setBusy(false); }
  };

  const isFinal = week?.status === 'final';
  const isReadOnly = !canEdit || (isFinal && !forceEdit);

  const updateField = (dayIndex, field, value, opts = {}) => {
    if (isReadOnly || !week) return;
    const day = week.days[dayIndex];
    setWeek(prev => ({
      ...prev,
      days: prev.days.map((d, i) => (i === dayIndex ? { ...d, [field]: value } : d)),
    }));
    const key = `${day.id}-${field}`;
    clearTimeout(saveTimers.current[key]);
    const doSave = () => patchScheduleDay(day.id, { [field]: value, requesterRole: user?.role });
    if (opts.immediate) doSave();
    else saveTimers.current[key] = setTimeout(doSave, 600);
  };

  // Per metric: the week's total (sum of each day's low/high midpoint) ± 1 population
  // standard deviation of those 7 daily values — the total is the actual weekly load number,
  // the ±SD band around it gives the trainer a sense of how much that total could realistically
  // swing given how variable the days were. Clamped to 0 on the low end (physical load values).
  const weeklyTotals = useMemo(() => {
    if (!week) return null;
    const mid = (lo, hi) => ((Number(lo) || 0) + (Number(hi) || 0)) / 2;
    const sumSdRange = values => {
      const sum = values.reduce((a, v) => a + v, 0);
      const mean = sum / values.length;
      const variance = values.reduce((a, v) => a + (v - mean) ** 2, 0) / values.length;
      const sd = Math.sqrt(variance);
      return [Math.max(0, sum - sd), sum + sd];
    };
    return {
      td: sumSdRange(week.days.map(d => mid(d.td_low, d.td_high))),
      hid: sumSdRange(week.days.map(d => mid(d.hid_low, d.hid_high))),
      sd: sumSdRange(week.days.map(d => mid(d.sd_low, d.sd_high))),
      short_actions: sumSdRange(week.days.map(d => mid(d.short_actions_low, d.short_actions_high))),
      hmld: sumSdRange(week.days.map(d => mid(d.hmld_low, d.hmld_high))),
    };
  }, [week]);

  const chartData = useMemo(() => {
    if (!week) return [];
    return week.days.map((d, i) => {
      const lo = Number(d.hmld_low) || 0, hi = Number(d.hmld_high) || 0;
      return { label: DAY_NAMES[i], value: (lo + hi) / 2, isMatch: d.is_match_day };
    });
  }, [week]);

  const maxChartValue = useMemo(() => Math.max(0, ...chartData.map(d => d.value)), [chartData]);

  if (loading || !selectedWeekStart) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', color: 'var(--text-muted)', fontSize: '0.9rem' }}>
        Loading…
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 1500, margin: '0 auto', paddingBottom: 40 }}>
      {/* ── Header ── */}
      <div style={{ marginBottom: 16 }}>
        <h2 style={{ fontSize: '1.2rem', fontWeight: 700, color: 'var(--text-primary)', margin: 0 }}>Week Schedule</h2>
        <p style={{ fontSize: '0.83rem', color: 'var(--text-muted)', margin: '4px 0 0' }}>
          Auto-filled training plan, MD types, and targets — fill in the rest per day.
        </p>
      </div>

      {/* ── Week selector / actions ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18, flexWrap: 'wrap' }}>
        <button onClick={goPrev} style={navBtn}>‹</button>
        <span style={{ fontSize: '0.92rem', fontWeight: 700, color: 'var(--text-primary)', minWidth: 140, textAlign: 'center' }}>
          Week of {fmtWeekRange(selectedWeekStart)}
        </span>
        <button onClick={goNext} style={navBtn}>›</button>

        {week && (
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 5,
            fontSize: '0.68rem', fontWeight: 700, padding: '3px 9px', borderRadius: 99, textTransform: 'uppercase',
            background: isFinal ? 'rgba(16,185,129,0.15)' : 'rgba(245,158,11,0.15)',
            color: isFinal ? '#10b981' : '#f59e0b',
          }}>
            {isReadOnly ? <Lock size={11} /> : <Unlock size={11} />}
            {week.status}
          </span>
        )}

        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {week && (
            <button onClick={handlePrint} style={actionBtnSecondary}>Print / PDF</button>
          )}
          {!week && canEdit && previousWeek && (
            <button onClick={handleDuplicatePrevious} disabled={busy} style={actionBtnSecondary}>
              Duplicate previous week
            </button>
          )}
          {canEdit && (!week || !isReadOnly) && (
            <button onClick={handleCreate} disabled={busy} style={actionBtn}>Create week</button>
          )}
          {week && canEdit && (
            <>
              <button onClick={handleResync} disabled={busy} style={actionBtnSecondary}>Resync</button>
              {!isReadOnly && (
                <button onClick={handleClearWeek} disabled={busy} style={actionBtnDanger}>Clear week</button>
              )}
              {isFinal && (
                <button onClick={() => setForceEdit(v => !v)} style={actionBtnSecondary}>
                  {forceEdit ? 'Stop editing' : 'Edit anyway'}
                </button>
              )}
              {!isFinal && (
                <button onClick={handleFinalize} disabled={busy} style={actionBtn}>Finalize week</button>
              )}
            </>
          )}
        </div>
      </div>

      {!week && (
        <div style={{ ...card, padding: 48, textAlign: 'center' }}>
          <p style={{ margin: 0, fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
            {canEdit ? 'No schedule yet for this week.' : 'This week has not been finalized yet.'}
          </p>
        </div>
      )}

      {week && (
        <>
          {/* ── Grid ── one shared grid so every day's rows (header/body/notes/min-rpe/
              targets) line up horizontally across all 7 columns, regardless of how much
              content each day actually has (match days skip most of it). ── */}
          <div key={resetKey} style={{
            display: 'grid', gridTemplateColumns: GRID_COLS, gridTemplateRows: 'auto auto auto auto auto',
            columnGap: GRID_GAP, rowGap: GRID_GAP, marginBottom: 20,
          }}>
            {week.days.flatMap((day, i) => dayColumnCells({ day, dayIndex: i, isReadOnly, updateField, opponents, openGymModal: setGymModalDayIndex }))}

            {/* Weekly totals column — spans the same row span as the day cards */}
            <div style={{ ...card, gridColumn: '8 / 9', gridRow: '1 / 6', padding: 14 }}>
              <div style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--text-primary)', marginBottom: 10 }}>Weekly total</div>
              {weeklyTotals && [
                ['TD', weeklyTotals.td], ['HID', weeklyTotals.hid], ['SD', weeklyTotals.sd],
                ['Short act.', weeklyTotals.short_actions], ['HMLD', weeklyTotals.hmld],
              ].map(([lbl, [lo, hi]]) => (
                <div key={lbl} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.76rem', marginBottom: 8 }}>
                  <span style={{ color: 'var(--text-muted)' }}>{lbl}</span>
                  <span style={{ color: 'var(--text-secondary)', fontWeight: 600 }}>{fmtRange(lo, hi)}</span>
                </div>
              ))}
            </div>
          </div>

          {/* ── Intensity chart ── plain CSS bars on the exact same column template as the
              day grid above (same GRID_COLS/GRID_GAP), so each bar sits directly under its
              day's column instead of drifting out of alignment the way a recharts
              ResponsiveContainer (with its own internal axis width/margins) would. ── */}
          <div style={{ ...card, padding: '16px 20px 14px' }}>
            <div style={{ fontSize: '0.82rem', fontWeight: 700, color: 'var(--text-primary)', marginBottom: 14 }}>
              HMLD intensity (target midpoint per day)
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: GRID_COLS, columnGap: GRID_GAP }}>
              {chartData.map((d, i) => {
                const h = maxChartValue > 0 ? Math.max(2, Math.round((d.value / maxChartValue) * CHART_BAR_MAX)) : 2;
                return (
                  <div key={i} style={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end',
                    height: CHART_BAR_MAX + 36,
                  }}>
                    <span style={{ fontSize: '0.68rem', color: 'var(--text-muted)', marginBottom: 4 }}>{fmt0(d.value)}</span>
                    <div style={{
                      width: '60%', height: h, borderRadius: '3px 3px 0 0',
                      background: d.isMatch ? '#ef4444' : '#3b82f6',
                    }} />
                    <span style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', marginTop: 6, fontWeight: 600 }}>{d.label}</span>
                  </div>
                );
              })}
              <div />
            </div>
          </div>
        </>
      )}

      {week && gymModalDayIndex != null && (
        <GymBreakdownModal
          day={week.days[gymModalDayIndex]} dayIndex={gymModalDayIndex} players={players}
          isReadOnly={isReadOnly} updateField={updateField}
          onClose={() => setGymModalDayIndex(null)}
        />
      )}
    </div>
  );
}

const navBtn = {
  width: 30, height: 30, borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-card)',
  color: 'var(--text-secondary)', fontSize: '1rem', cursor: 'pointer', lineHeight: 1,
};
const actionBtn = {
  padding: '7px 14px', borderRadius: 8, border: '1px solid #3b82f6', background: '#3b82f6',
  color: '#fff', fontSize: '0.78rem', fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer',
};
const actionBtnSecondary = {
  padding: '7px 14px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg-card)',
  color: 'var(--text-secondary)', fontSize: '0.78rem', fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer',
};
const actionBtnDanger = {
  padding: '7px 14px', borderRadius: 8, border: '1px solid rgba(239,68,68,0.4)', background: 'rgba(239,68,68,0.1)',
  color: '#ef4444', fontSize: '0.78rem', fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer',
};
